import { AlignmentType, Document, HeadingLevel, Packer, Paragraph, Table, TableCell, TableRow, WidthType } from "docx";
import { Box, Typography } from "@mui/material";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { DataTableCard, M3Button, FormSection, FieldGrid, M3Field, Chip, ChartCard } from "../components/m3";
import LongTermNetZeroPathwayChart from "./LongTermNetZeroPathwayChart";
import NetZeroEmissionsBreakdownCharts from "./NetZeroEmissionsBreakdownCharts";
import AgGrid from "../components/grid/AgGrid";
import { leftAlignColDef, centerAlignColDef, rightAlignColDef } from "../components/grid/columnDefinitions/columnDefinitions";
import { saveAs } from "file-saver";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../authentication/AuthProvider";
import { useContextProvider } from "../components/page-context/ContextProvider";
import config from "../config";
import { LongTermTool } from "../db_access/long_term_tool";
import { NetZeroTool } from "../db_access/net_zero_tool";
import { Supply_chain } from "../db_access/supply_chain";
import { Structure } from "../db_access/structure";
import Grid from "../environment-page-components/Grid";
import { scope1Categories, scope2Categories, scope3Categories } from "../pages/Organization/ScopeCategory";
import "../style/components-style/NetZeroBottomTool.css";

const sectorPathwayList = ["Cross-sector pathway", "FLAG (Agriculture only)", "Iron and steel", "Cement", "Services - Buildings", "Residential - Buildings"];

const percentageReductionData = {
  "Cross-sector pathway": { S1AbsTarget: 0.9, S12AbsTarget: 0.9 },
  "FLAG (Agriculture only)": { S1AbsTarget: 0.72, S12AbsTarget: 0.72 },
  "Iron and steel": { S1AbsTarget: 0.9122, S12AbsTarget: 0.9299 },
  Cement: { S1AbsTarget: 0.946, S12AbsTarget: 0.9495 },
  "Services - Buildings": { S1AbsTarget: 0.9857, S12AbsTarget: 0.996 },
  "Residential - Buildings": { S1AbsTarget: 0.9468, S12AbsTarget: 0.9789 },
};

function NetZeroBottomLongTermTool({
  selectedRow,
  page,
  yearlyRowData = [],
  yearlyColumnDefs = [],
  yearMapByColumnField = {},
  organizations = [],
  selectedOrganizationIds = [],
}) {
  const { t } = useTranslation();
  const {
    selectedProducts,
    setSelectedProducts,
    setSelectedProductIds,
    refresh,
    setRefresh,
    columnVisibilities,
    isSaveClicked,
    setIsSaveVisible,
    setIsLoading,
    setIsSaveClicked,
    setIsSaveError,
    envFactorOptions,
  } = useContextProvider();
  const [isOpen, setIsOpen] = useState(true);
  const [baseYear, setBaseYear] = useState("");
  const [targetYear, setTargetYear] = useState("");
  const [actionYear, setActionYear] = useState("");

  const [mostRecentYear, setMostRecentYear] = useState("");
  const [mryScope1Emission, setMryScope1Emission] = useState(0.0);
  const [mryScope2Emission, setMryScope2Emission] = useState(0.0);
  const [mryScope3Emission, setMryScope3Emission] = useState(0.0);
  const [mryTotalEmissions, setMryTotalEmissions] = useState(0.0);

  const [sectorPathway, setSectorPathway] = useState("");
  const [scope1Emission, setScope1Emission] = useState(0.0);
  const [scope2Emission, setScope2Emission] = useState(0.0);
  const [scope3Emission, setScope3Emission] = useState(0.0);
  const [totalEmissions, setTotalEmissions] = useState(0.0);
  const isSupplyChainPage = page === "Supply Chain";
  const templateId = selectedRow?.id ?? null;
  const [selectedTargetCoverage, setSelectedTargetCoverage] = useState("");

  const [baseYearIntensity, setBaseYearIntensity] = useState(0.0);
  const [targetYearIntensity, setTargetYearIntensity] = useState(0.0);
  const [percentageReduction, setPercentReduction] = useState(0);
  const [longTermSBTFormulation, setLongTermSBTFormulation] = useState("");

  const [rowData, setRowData] = useState([]);
  const gridApiRef = useRef(null);

  const { user } = useAuth();
  const userId = user ? user.username : null;

  // Extract years dynamically from the active Yearly GHG AgGrid table
  const tableYears = useMemo(() => {
    const yearsSet = new Set();
    if (yearMapByColumnField) {
      Object.values(yearMapByColumnField).forEach((y) => {
        if (y && /^\d{4}$/.test(String(y))) yearsSet.add(String(y));
      });
    }
    if (yearlyColumnDefs) {
      yearlyColumnDefs.forEach((col) => {
        if (col.headerName) {
          const match = col.headerName.match(/\b(20\d\d)\b/);
          if (match) yearsSet.add(match[1]);
        }
      });
    }
    return Array.from(yearsSet).sort();
  }, [yearlyColumnDefs, yearMapByColumnField]);

  const currentYear = new Date().getFullYear();
  const baseYearList = useMemo(() => {
    if (tableYears.length > 0) return tableYears;
    return Array.from({ length: currentYear - 2020 + 1 }, (_, i) => (2020 + i).toString());
  }, [tableYears, currentYear]);

  const mostRecentYearList = useMemo(() => {
    if (tableYears.length > 0) return tableYears;
    return Array.from({ length: 6 }, (_, i) => (2020 + i).toString());
  }, [tableYears]);

  const targetYearList = Array.from({ length: 26 }, (_, i) => (2025 + i).toString());

  // Function to pull Scope 1, Scope 2, Scope 3 directly from the Yearly Based GHG table
  const getEmissionsForYear = useCallback((targetYear) => {
    if (!targetYear || !yearlyRowData || yearlyRowData.length === 0) return null;

    let targetField = null;
    if (yearMapByColumnField) {
      targetField = Object.keys(yearMapByColumnField).find(
        (f) => String(yearMapByColumnField[f]) === String(targetYear)
      );
    }
    if (!targetField && yearlyColumnDefs) {
      const foundDef = yearlyColumnDefs.find(
        (c) => c.headerName && c.headerName.includes(String(targetYear))
      );
      if (foundDef) targetField = foundDef.field;
    }
    if (!targetField) return null;

    const s1Row = yearlyRowData.find(
      (r) => String(r.scope || "").trim().toLowerCase() === "scope 1"
    );
    const s2Row = yearlyRowData.find(
      (r) => String(r.scope || "").trim().toLowerCase() === "scope 2"
    );
    const s3Rows = yearlyRowData.filter((r) => {
      const s = String(r.scope || "").trim().toLowerCase();
      return s.startsWith("scope 3") && !s.includes("total");
    });

    const s1 = Number((Number(s1Row?.[targetField]) || 0).toFixed(2));
    const s2 = Number((Number(s2Row?.[targetField]) || 0).toFixed(2));
    const s3 = Number(s3Rows.reduce((sum, r) => sum + (Number(r[targetField]) || 0), 0).toFixed(2));

    return { s1, s2, s3, total: Number((s1 + s2 + s3).toFixed(2)) };
  }, [yearlyRowData, yearlyColumnDefs, yearMapByColumnField]);

  // Synchronize baseYear and mostRecentYear with available table years
  useEffect(() => {
    if (tableYears.length > 0) {
      if (!baseYear || !tableYears.includes(baseYear)) {
        setBaseYear(tableYears[0]);
      }
      if (tableYears.length > 1) {
        if (!mostRecentYear || !tableYears.includes(mostRecentYear) || mostRecentYear === tableYears[0]) {
          setMostRecentYear(tableYears[tableYears.length - 1]);
        }
      } else if (!mostRecentYear) {
        setMostRecentYear(tableYears[0]);
      }
    }
  }, [tableYears]);

  // Pull Base Year emissions dynamically
  useEffect(() => {
    if (!baseYear) return;
    const em = getEmissionsForYear(baseYear);
    if (em && em.total > 0) {
      setScope1Emission(em.s1);
      setScope2Emission(em.s2);
      setScope3Emission(em.s3);
      setTotalEmissions(em.total);
    }
  }, [baseYear, getEmissionsForYear]);

  // Pull MRY emissions dynamically
  useEffect(() => {
    if (!mostRecentYear) return;
    const em = getEmissionsForYear(mostRecentYear);
    if (em) {
      setMryScope1Emission(em.s1);
      setMryScope2Emission(em.s2);
      setMryScope3Emission(em.s3);
      setMryTotalEmissions(em.total);
    }
  }, [mostRecentYear, getEmissionsForYear]);

  const actionYearList = useMemo(() => {
    if (!baseYear || !targetYear) return [];

    const start = parseInt(baseYear, 10);
    const end = parseInt(targetYear, 10);

    if (isNaN(start) || isNaN(end) || start >= end) return [];

    return Array.from({ length: end - start }, (_, i) => start + i);
  }, [baseYear, targetYear]);

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
  }, []);

  const resetForm = () => {
    setBaseYear("");
    setTargetYear("");
    setMostRecentYear("");
    setSectorPathway("");
    setSelectedTargetCoverage("");
    setMryScope1Emission(0.0);
    setMryScope2Emission(0.0);
    setMryScope3Emission(0.0);
    setMryTotalEmissions(0.0);
  };

  useEffect(() => {
    if (!selectedRow || !selectedRow.id) {
      setRowData([]);
      resetForm();
      return;
    }

    loadLongTermToolInputs();
    fetchData();
  }, [selectedRow]);

  useEffect(() => {
    if (!selectedRow || !selectedRow.id) return;
    const emissionFetch = page === "Supply Chain"
      ? Supply_chain.getEmissions(selectedRow.id)
      : Structure.getEmissions(selectedRow.id);
    emissionFetch
      .then((res) => res.json())
      .then((data) => {
        const s1Emissions = data.filter((d) => scope1Categories.includes(d.category));
        const s2Emissions = data.filter((d) => scope2Categories.includes(d.category));
        const s3Emissions = data.filter((d) => scope3Categories.includes(d.category));
        const s1_sum = s1Emissions.reduce((acc, curr) => acc + (parseFloat(curr.gwp) || 0), 0);
        const s2_sum = s2Emissions.reduce((acc, curr) => acc + (parseFloat(curr.gwp) || 0), 0);
        const s3_sum = s3Emissions.reduce((acc, curr) => acc + (parseFloat(curr.gwp) || 0), 0);
        // Fallback if not populated from yearly table
        if (totalEmissions <= 0) {
          setScope1Emission(s1_sum / 1000 || 0.0);
          setScope2Emission(s2_sum / 1000 || 0.0);
          setScope3Emission(s3_sum / 1000 || 0.0);
          setTotalEmissions((s1_sum + s2_sum + s3_sum) / 1000 || 0.0);
        }
      });
  }, [page, selectedRow]);

  useEffect(() => {
    if (sectorPathway && percentageReductionData[sectorPathway]) {
      const reduction = selectedTargetCoverage === "Scope 1" ? percentageReductionData[sectorPathway].S1AbsTarget : percentageReductionData[sectorPathway].S12AbsTarget;
      setPercentReduction(reduction);
    } else {
      setPercentReduction(0.0);
    }
  }, [sectorPathway, selectedTargetCoverage]);

  useEffect(() => {
    if (selectedRow) {
      const companyName = selectedRow.product_service ? selectedRow.product_service : selectedRow.organization ? selectedRow.organization : selectedRow.corporate;

      if (sectorPathway === "FLAG (Agriculture only)") {
        const SBTFormulation = `${companyName} commits to reduce ${selectedTargetCoverage} emissions ${(percentageReduction * 100).toFixed(
          2
        )} by ${targetYear} from a ${baseYear} base year`;
        setLongTermSBTFormulation(SBTFormulation);
      } else {
        const SBTFormulation = `${companyName} commits to reduce emissions from agriculture, forestry, and land-use ${(percentageReduction * 100).toFixed(
          2
        )} by ${targetYear} from a ${baseYear} base year`;
        setLongTermSBTFormulation(SBTFormulation);
      }
    }
  }, [selectedRow, sectorPathway, selectedTargetCoverage, percentageReduction, targetYear, baseYear]);

  const toggleContainer = () => setIsOpen(!isOpen);

  // ---------- LTT API helpers (using LongTermTool like ShortTermTool) ----------

  const fetchLongTermToolInputs = async (templateId) => {
    try {
      let inputData = null;
      if (page === "Supply Chain") {
        const response = await LongTermTool.get_by_supply_chain_id(templateId);
        inputData = await response.json();
      } else {
        const response = await LongTermTool.get_by_structure_id(templateId);
        inputData = await response.json();
      }
      if (!inputData || inputData.length === 0) {
        await addLongTermToolInputs(templateId);
      }

      return inputData;
    } catch (error) {
      console.error("Error fetching long-term tool inputs:", error);
      return null;
    }
  };

  const loadLongTermToolInputs = async () => {
    if (!selectedRow || !selectedRow.id) {
      resetForm();
      return;
    }

    const longTermToolInputsData = await fetchLongTermToolInputs(selectedRow.id);
    if (longTermToolInputsData && longTermToolInputsData.length > 0) {
      const data = longTermToolInputsData[0];

      setBaseYear(data.base_year || "");
      setTargetYear(data.target_year || "");
      setSectorPathway(data.sector_pathway || "");
      setSelectedTargetCoverage(data.target_coverage || "");
    } else {
      resetForm();
    }
  };

  const addLongTermToolInputs = async (templateId) => {
    try {
      const ltt = {
        supply_chain_id: null,
        user_id: userId,
        structure_id: null,
        corporate_id: null,
        base_year: null,
        target_year: null,
        target_coverage: null,
        sector_pathway: null,
      };
      if (page === "Supply Chain") {
        ltt.supply_chain_id = templateId;
      } else {
        ltt.structure_id = templateId;
      }

      await LongTermTool.new(ltt);
    } catch (err) {
      console.error("Error creating long-term tool inputs:", err);
    }
  };

  const saveLongTermToolInputs = async () => {
    try {
      if (!selectedRow || !selectedRow.id) {
        console.warn("No template selected. Cannot update long-term tool inputs.");
        return;
      }

      let payload = {
        supply_chain_id: null,
        user_id: userId,
        structure_id: null,
        corporate_id: null,
        base_year: baseYear || null,
        target_year: targetYear || null,
        target_coverage: selectedTargetCoverage || null,
        sector_pathway: sectorPathway || null,
      };
      let result = [];

      if (page === "Supply Chain") {
        payload.supply_chain_id = selectedRow.id;
        result = await LongTermTool.update_ltt_by_supply_chain(payload);
      } else {
        payload.structure_id = selectedRow.id;
        result = await LongTermTool.update_ltt_by_structure(payload);
      }
      if (result && result.status === 200) {
        let fresh = [];
        if (page === "Supply Chain") {
          fresh = await LongTermTool.get_by_supply_chain_id(selectedRow.id);
        } else {
          fresh = await LongTermTool.get_by_structure_id(selectedRow.id);
        }
        const data = await fresh.json();
        const first = data?.[0];
        if (first) {
          setBaseYear(first.base_year || "");
          setTargetYear(first.target_year || "");
          setSectorPathway(first.sector_pathway || "");
          setSelectedTargetCoverage(first.target_coverage || "");
        }
      } else {
        console.error("Failed to save long-term tool inputs.");
        setIsSaveError(true);
      }
    } catch (error) {
      console.error("Error updating long-term tool inputs:", error);
      setIsSaveError(true);
    }
  };

  const fetchData = () => {
    if (!templateId) {
      setRowData([]);
      return;
    }

    const req = isSupplyChainPage ? NetZeroTool.get_by_supply_chain_id_and_type(templateId, "long-term") : NetZeroTool.get_by_structure_id_and_type(templateId, "long-term");

    req
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok: " + response.statusText);
        return response.json();
      })
      .then((data) => {
        setRowData(data || []);
      })
      .catch((error) => console.error("Error fetching data:", error));
  };

  const addRow = () => {
    if (!gridApiRef.current) return;

    if (!templateId) {
      window.alert("No template selected.");
      return;
    }

    if (actionYear === "") {
      window.alert("Select the year of the newly added action!");
      return;
    }

    const newRow = {
      id: undefined, // backend will create
      supply_chain_id: isSupplyChainPage ? templateId : null,
      structure_id: isSupplyChainPage ? null : templateId,
      corporate_id: null,
      type: "long-term",
      action: "",
      category: "",
      year: parseInt(actionYear, 10),
      details: "",
      estimated_ghg: 0,
    };

    NetZeroTool.new(newRow)
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok: " + response.statusText);
        return response.json();
      })
      .then(() => {
        // safest is to refetch from backend so IDs etc are correct
        fetchData();
      })
      .catch((error) => console.error("Error adding data:", error));
  };

  const deleteSelectedRows = () => {
    if (!gridApiRef.current) return;

    const selectedData = gridApiRef.current.getSelectedRows();
    NetZeroTool.delete_by_ids(selectedData.map((row) => row.id)).finally(() => fetchData());
  };

  const saveTable = () => {
    if (!gridApiRef.current) return;

    const allRows = gridApiRef.current.getRenderedNodes ? (gridApiRef.current.getRenderedNodes() || []).map((node) => node.data) : [];

    Promise.all(allRows.map((row) => NetZeroTool.update(row)))
      .then(() => {
        fetchData();
      })
      .catch((error) => console.error("Error updating data:", error));
  };

  useEffect(() => {
    if (isSaveClicked) {
      saveLongTermToolInputs();
      saveTable();
      setIsSaveClicked(false);
      setIsLoading(false);
    }
  }, [isSaveClicked]);

  const columnDefs = [
    { field: "id", hide: true, filter: false },
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      headerName: t("Sl no."),
      field: "sno",
      width: 80,
      pinned: "left",
      valueGetter: (params) => params.node.rowIndex + 1,
      sortable: false,
      filter: false,
    },
    {
      headerName: t("Action"),
      field: "action",
      flex: 1,
      editable: true,
      cellEditor: "agTextCellEditor",
      filter: false,
    },
    {
      headerName: t("Term"),
      field: "tool_type",
      flex: 1,
      editable: false,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: ["Long-term", "Short-term", "Freight"],
      },
      filter: false,
    },
    {
      headerName: t("Category"),
      field: "category",
      flex: 1,
      editable: true,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: ["Operational", "Strategic", "Tactical"],
      },
      filter: false,
    },
    {
      headerName: t("Year"),
      field: "year",
      flex: 1,
      editable: false,
      filter: false,
    },
    {
      headerName: t("Details"),
      field: "details",
      flex: 4,
      editable: true,
      cellEditor: "agTextCellEditor",
      filter: false,
    },
    {
      headerName: t("Estimated GHG reductions (ton CO₂e)"),
      field: "estimated_ghg",
      flex: 1,
      editable: true,
      cellStyle: { textAlign: "right" },
      cellEditor: "agTextCellEditor",
      cellEditorParams: {
        useFormatter: true,
        inputType: "number",
      },
      filter: false,
    },
  ];

  const freightToolGridProps = {
    columnDefs,
    rowData,
    rowSelection: "multiple",
    onGridReady,
    domLayout: "autoHeight",
    onSelectionChanged: () => {
      setIsSaveVisible(true);
    },
    onCellValueChanged: ({ newValue, oldValue }) => {
      if (newValue !== oldValue) {
        setIsSaveVisible(true);
      }
    },
    gridOptions: {
      suppressCellSelection: false,
    },
  };

  const generateTable = (data) => {
    return new Table({
      columnWidths: [4000, 6000],
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
      rows: data.map(
        (row) =>
          new TableRow({
            children: row.map(
              (cell) =>
                new TableCell({
                  children: [new Paragraph({ text: cell, spacing: { after: 100 } })],
                  width: {
                    size: 50,
                    type: WidthType.PERCENTAGE,
                  },
                })
            ),
          })
      ),
    });
  };

  const longTermToolExport = async () => {
    try {
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              new Paragraph({
                text: "Net Zero Long-Term Tool Report",
                heading: HeadingLevel.TITLE,
                alignment: AlignmentType.CENTER,
              }),

              new Paragraph({
                text: "Section 1.1. Input data (absolute targets)",
                heading: HeadingLevel.HEADING_1,
                spacing: { after: 200 },
              }),

              generateTable([
                ["Target Coverage", selectedTargetCoverage || "N/A"],
                ["Target Setting Method", "Absolute contraction"],
                ["Base Year", baseYear || "N/A"],
                ["Target Year", targetYear || "N/A"],
                ["Sector Pathway", sectorPathway || "N/A"],
                ["Scope 1 Emissions", scope1Emission + " tCO₂e" || "0 tCO₂e"],
                ["Scope 2 Emissions", scope2Emission + " tCO₂e" || "0 tCO₂e"],
                ["Scope 3 Emissions", scope3Emission + " tCO₂e" || "0 tCO₂e"],
                ["Total Emissions", totalEmissions + " tCO₂e" || "0.00 tCO₂e"],
              ]),

              new Paragraph({
                text: "Section 1.2. Absolute target results",
                heading: HeadingLevel.HEADING_1,
                spacing: { after: 200 },
              }),

              generateTable([
                ["", "Base Year", "Target Year", "% Intensity Reduction"],
                [
                  "Company | 0 intensity (tCO₂e/unit of value added)",
                  totalEmissions.toFixed(2) || "0.00",
                  (totalEmissions * (1 - percentageReduction)).toFixed(2) || "#N/A",
                  (percentageReduction * 100).toFixed(2) + "%",
                ],
                ["Long Term SBT formulation", longTermSBTFormulation],
              ]),

              new Paragraph({
                text: "Actions Table",
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 200, after: 200 },
              }),

              generateTable([
                ["Sl no.", "Action", "Term", "Category", "Year", "Details", "ESTIMATED GHG REDUCTIONS (ton CO₂e)"],
                ...rowData.map((action, index) => [
                  String(index + 1),
                  action.action || "N/A",
                  action.tool_type || "N/A",
                  action.category || "N/A",
                  String(action.year) || "N/A",
                  action.details || "N/A",
                  String(action.estimated_ghg) || "N/A",
                ]),
              ]),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, "Long_Term_Tool_Report.docx");
    } catch (error) {
      console.error("Error generating DOCX:", error);
      alert("An error occurred while generating the DOCX file.");
    }
  };

  const selectStyle = {
    width: "100%",
    height: "40px",
    borderRadius: "8px",
    padding: "0 12px",
    fontFamily: "Poppins, sans-serif",
    fontSize: "13.5px",
    fontWeight: 500,
    color: "var(--m3-on-surface, #1b1c1a)",
    backgroundColor: "var(--m3-surface, #ffffff)",
    border: "1px solid var(--m3-outline-variant, #e5e7eb)",
    outline: "none",
    cursor: "pointer",
  };

  const labelStyle = {
    display: "block",
    fontSize: "12px",
    fontWeight: 600,
    color: "var(--m3-on-surface-variant, #454840)",
    marginBottom: "6px",
  };

  const targetResultRowData = useMemo(() => [
    {
      metric: `${t("Company")} | ${selectedTargetCoverage || "Scopes 1, 2 & 3"} (tCO₂e)`,
      baseYear: (totalEmissions || 0).toFixed(2),
      targetYear: percentageReduction ? ((totalEmissions || 0) * (1 - percentageReduction)).toFixed(2) : "—",
      reduction: percentageReduction ? (percentageReduction * 100).toFixed(2) : "0.00",
      sbtFormulation: longTermSBTFormulation || "—",
    },
  ], [totalEmissions, percentageReduction, longTermSBTFormulation, selectedTargetCoverage, t]);

  const targetResultColDefs = useMemo(() => [
    {
      headerName: t("Metric"),
      field: "metric",
      ...leftAlignColDef,
      flex: 1.8,
      minWidth: 220,
      cellStyle: { fontWeight: 600 },
    },
    {
      headerName: `${t("Base Year")} (${baseYear || "—"})`,
      field: "baseYear",
      ...centerAlignColDef,
      flex: 1,
      minWidth: 120,
    },
    {
      headerName: `${t("Target Year")} (${targetYear || "—"})`,
      field: "targetYear",
      ...centerAlignColDef,
      flex: 1,
      minWidth: 120,
    },
    {
      headerName: t("% Intensity Reduction"),
      field: "reduction",
      ...rightAlignColDef,
      flex: 1.2,
      minWidth: 150,
      cellRenderer: (params) => {
        if (!params.value) return "—";
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", alignItems: "center" }}>
            <Chip tone="success" size="sm">
              {params.value}%
            </Chip>
          </Box>
        );
      },
    },
    {
      headerName: t("Long Term SBT Formulation"),
      field: "sbtFormulation",
      ...leftAlignColDef,
      flex: 2,
      minWidth: 220,
    },
  ], [baseYear, targetYear, t]);

  return (
    <div className="absolute-target-section">
      <div className="net-zero-tool-header export-ignore">
        <h2 onClick={toggleContainer}>Net Zero Long Term Tool</h2>
      </div>
      {isOpen && (
        <div className="tool-section">
          <FormSection
            title={t("Input Data (Absolute Targets)")}
            action={
              <M3Button variant="outlined" size="sm" onClick={resetForm}>
                {t("Reset")}
              </M3Button>
            }
            className="export-ignore"
          >
            <FieldGrid columns={4}>
              <div>
                <label style={labelStyle}>{t("Target Coverage")}</label>
                <select
                  style={selectStyle}
                  value={selectedTargetCoverage}
                  onChange={(e) => {
                    setSelectedTargetCoverage(e.target.value);
                    setIsSaveVisible(true);
                  }}
                >
                  <option value="">{t("Select Coverage")}</option>
                  <option value="Scope 1">Scope 1</option>
                  <option value="Scope 1 & 2">Scope 1 & 2</option>
                  <option value="Scope 3">Scope 3</option>
                  <option value="Scope 1,2 & 3">Scope 1,2 & 3</option>
                </select>
              </div>

              <M3Field
                label={t("Target Setting Method")}
                value="Absolute contraction"
                disabled
              />

              <div>
                <label style={labelStyle}>{t("Base Year")}</label>
                <select
                  style={selectStyle}
                  value={baseYear}
                  onChange={(e) => {
                    setBaseYear(e.target.value);
                    setIsSaveVisible(true);
                  }}
                >
                  <option value="">{t("Select Year")}</option>
                  {baseYearList.map((year, index) => (
                    <option key={index} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>{t("Target Year")}</label>
                <select
                  style={selectStyle}
                  value={targetYear}
                  onChange={(e) => {
                    setTargetYear(e.target.value);
                    setIsSaveVisible(true);
                  }}
                >
                  <option value="">{t("Select Year")}</option>
                  {targetYearList.map((year, index) => (
                    <option key={index} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>{t("Most Recent Year (MRY)")}</label>
                <select
                  style={selectStyle}
                  value={mostRecentYear}
                  onChange={(e) => {
                    setMostRecentYear(e.target.value);
                    setIsSaveVisible(true);
                  }}
                >
                  <option value="">{t("Select Year")}</option>
                  {mostRecentYearList.map((year, index) => (
                    <option key={index} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>{t("Sector Pathway")}</label>
                <select
                  style={selectStyle}
                  value={sectorPathway}
                  onChange={(e) => {
                    setSectorPathway(e.target.value);
                    setIsSaveVisible(true);
                  }}
                >
                  <option value="">{t("Select Sector Pathway")}</option>
                  {sectorPathwayList.map((year, index) => (
                    <option key={index} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <M3Field
                label={`${t("Base Year")} (${baseYear || "—"}) ${t("Emissions")}`}
                value={totalEmissions.toFixed(2)}
                suffix="tCO₂e"
                disabled
              />
              <M3Field
                label={`${t("MRY")} (${mostRecentYear || "—"}) ${t("Emissions")}`}
                value={(mryTotalEmissions || 0).toFixed(2)}
                suffix="tCO₂e"
                disabled
              />
            </FieldGrid>
          </FormSection>

          <Box sx={{ mt: 3 }} className="export-ignore">
            <DataTableCard title={t("Absolute Target Results")}>
              <AgGrid
                columnDefs={targetResultColDefs}
                rowData={targetResultRowData}
                domLayout="autoHeight"
                headerCheckbox={false}
                checkboxes={false}
                pagination={false}
                suppressPagination={true}
              />
            </DataTableCard>
          </Box>

          {/* LONG-TERM NET ZERO PATHWAY (GRAPH 5) */}
          <Box sx={{ mt: 3 }} className="export-ignore">
            <ChartCard title={t("Long-Term Net Zero Pathway")}>
              <LongTermNetZeroPathwayChart
                baseYear={baseYear || (tableYears[0] ?? 2024)}
                baseYearEmissions={totalEmissions > 0 ? totalEmissions : 4016.91}
                mostRecentYear={mostRecentYear || (tableYears[1] ?? 2025)}
                mostRecentYearEmissions={mryTotalEmissions > 0 ? mryTotalEmissions : 3098.00}
                targetYear={targetYear || 2050}
                targetReductionPercent={percentageReduction || 0.90}
                unit="tCO₂e"
              />
            </ChartCard>
          </Box>

          {/* EMISSIONS SCOPE ANALYTICS (GRAPHS 1, 2, 3) */}
          <Box sx={{ mt: 3 }} className="export-ignore">
            <NetZeroEmissionsBreakdownCharts
              baseYear={baseYear || (tableYears[0] ?? 2024)}
              mostRecentYear={mostRecentYear || (tableYears[1] ?? 2025)}
              scope1Base={scope1Emission}
              scope2Base={scope2Emission}
              scope3Base={scope3Emission}
              scope1Mry={mryScope1Emission}
              scope2Mry={mryScope2Emission}
              scope3Mry={mryScope3Emission}
              unit="tCO₂e"
            />
          </Box>

          <Box sx={{ mt: 3 }} className="export-ignore">
            <DataTableCard
              title={t("Action Planning")}
              actions={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <M3Button variant="tonal" size="sm" onClick={addRow} icon={<FiPlus size={16} />}>
                    {t("Add row")}
                  </M3Button>
                  <M3Button variant="outlined" size="sm" onClick={deleteSelectedRows} icon={<FiTrash2 size={16} />}>
                    {t("Delete selected rows")}
                  </M3Button>
                  <Box
                    component="select"
                    value={actionYear}
                    onChange={(e) => setActionYear(e.target.value)}
                    sx={{
                      height: "36px",
                      px: 1.5,
                      borderRadius: "8px",
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "var(--m3-on-surface, #1b1c1a)",
                      bgcolor: "var(--m3-surface, #ffffff)",
                      border: "1px solid var(--m3-outline-variant, #e5e7eb)",
                      outline: "none",
                      cursor: "pointer",
                      "&:focus": {
                        borderColor: "var(--m3-primary, #659d33)",
                        boxShadow: "0 0 0 2px rgba(101, 157, 51, 0.2)",
                      },
                    }}
                  >
                    <option value="">{t("Select Action Year")}</option>
                    {actionYearList.map((year, index) => (
                      <option key={index} value={year}>
                        {year}
                      </option>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    * {t("Select action year between base year and target year")}
                  </Typography>
                </Box>
              }
            >
              <Grid {...freightToolGridProps} />
            </DataTableCard>
          </Box>
        </div>
      )}
    </div>
  );
}

export default NetZeroBottomLongTermTool;
